// src/commsReport/index.js — 📊 Comms Report (v125.22, reporting plan A1).
//
// Matt: "Admin and Dev tab — we need to incorporate reporting against the new
// features." This is the managers-app dashboard: one grid showing per-store +
// per-area engagement across the v125 comms features — 📰 News read, ☑️ Tasks
// done, 📋 Surveys answered, 📔 Journal written — each RAG-coloured. It reads the
// single GET /api/admin/comms-report endpoint (which reuses the tested per-feature
// engines) and is role-scoped server-side: an area manager only ever receives
// their own stores, so this bundle just renders whatever it's given.
//
// Read-only. No pay/HR data. A metric a store has no data for shows "—" (grey),
// never a misleading 0%.

import { ensureXlsx } from '../shared/ensure-xlsx.js';   // v136.8: the spreadsheet library loads only when it is used
import { CARRY_FLAG_DAYS } from '../shared/holiday-weekly.js';   // the holiday carry limit's one home (15 since 19 Oct 2026)

function esc(s) {
  return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}
function api(path, opts) {
  opts = opts || {};
  var base = (typeof DB !== 'undefined' && DB.workerUrl) ? DB.workerUrl() : '';
  var headers = { 'Content-Type': 'application/json' };
  var tk = (typeof DB !== 'undefined' && DB.getToken) ? DB.getToken() : null;
  if (tk) headers['Authorization'] = 'Bearer ' + tk;
  return fetch(base + path, { method: opts.method || 'GET', headers: headers, body: opts.body, cache: 'no-store' });
}

var _styled = false;
function ensureStyles() {
  if (_styled) return;
  _styled = true;
  var css = '' +
    '#commsReportRoot .cr-wrap{max-width:1000px}' +
    '#commsReportRoot .cr-head{display:flex;gap:12px;align-items:center;flex-wrap:wrap;margin-bottom:14px}' +
    '#commsReportRoot .cr-head h3{margin:0;font-size:15px;color:var(--brand)}' +
    '#commsReportRoot .cr-range{font-size:12.5px;color:var(--sub);display:flex;gap:6px;align-items:center;flex-wrap:wrap}' +
    '#commsReportRoot .cr-range input{padding:6px 8px;border:1px solid var(--border-strong);border-radius:8px;font-size:12.5px;font-family:inherit}' +
    '#commsReportRoot .cr-btn{background:var(--bg);border:none;border-radius:8px;padding:6px 12px;font-size:12.5px;font-weight:600;cursor:pointer;color:var(--text)}' +
    '#commsReportRoot .cr-legend{font-size:11.5px;color:var(--tiny);margin:0 0 10px}' +
    '#commsReportRoot .cr-legend .chip{display:inline-block;width:10px;height:10px;border-radius:3px;margin:0 3px 0 10px;vertical-align:middle}' +
    '#commsReportRoot .cr-scroll{overflow-x:auto;border:1px solid var(--border);border-radius:12px}' +
    '#commsReportRoot table{border-collapse:collapse;width:100%;font-size:12.5px;background:var(--card)}' +
    '#commsReportRoot th,#commsReportRoot td{padding:8px 10px;text-align:center;white-space:nowrap}' +
    '#commsReportRoot th{background:var(--surface-2);color:var(--sub);font-weight:700;position:sticky;top:0}' +
    '#commsReportRoot td.store,#commsReportRoot th.store{text-align:start;font-weight:600;color:var(--text)}' +
    '#commsReportRoot td.am{text-align:start;color:var(--sub)}' +
    '#commsReportRoot tr:nth-child(even) td{background:var(--surface-2)}' +
    '#commsReportRoot .cr-cell{display:inline-block;min-width:44px;border-radius:8px;padding:3px 6px;font-weight:700;color:#fff}' +
    '#commsReportRoot .cr-count{display:block;font-size:10px;font-weight:600;opacity:.85;line-height:1.2}' +
    '#commsReportRoot .rag-green{background:#009E3D}' +
    '#commsReportRoot .rag-amber{background:#F5A623}' +
    '#commsReportRoot .rag-red{background:#E24A4A}' +
    '#commsReportRoot .rag-none{background:var(--surface-3);color:var(--tiny)}' +
    '#commsReportRoot .cr-sub{border-top:2px solid var(--border)}' +
    '#commsReportRoot .cr-sub td{font-weight:700;background:var(--ap-row-hi)}' +
    '#commsReportRoot .cr-row:hover td{background:var(--ap-row-hi)}' +
    '#commsReportRoot .cr-drill td{background:var(--surface-2);border-bottom:2px solid var(--border)}' +
    '#commsReportRoot .cr-dwrap{padding:6px 4px;font-size:12.5px;color:var(--text)}' +
    '#commsReportRoot .cr-dhead{font-weight:700;margin-bottom:6px}' +
    '#commsReportRoot .cr-dsec{margin-bottom:8px}' +
    '#commsReportRoot .cr-ditem{margin:3px 0 0 14px}' +
    '#commsReportRoot .cr-dtitle{font-weight:600}' +
    '#commsReportRoot .cr-dim{color:var(--tiny)}' +
    '#commsReportRoot .cr-ok{color:var(--brand);font-weight:600}' +
    '#commsReportRoot .cr-more{color:var(--tiny);font-style:italic}' +
    '#commsReportRoot .cr-note{font-size:11.5px;color:var(--tiny);margin-top:10px}';
  var el = document.createElement('style');
  el.id = 'commsReportStyles';
  el.textContent = css;
  document.head.appendChild(el);
}

function root() { return document.getElementById('commsReportRoot'); }

// RAG for the roll-up rows (per-store cells already carry their server RAG).
// Mirrors src/worker/comms-report.js DEFAULT_THRESHOLDS 90/75 — kept in sync by
// tests/comms-report-ui_test.js so the grid and the endpoint never disagree.
function ragFor(pct) {
  if (pct == null || isNaN(pct)) return 'none';
  if (pct >= 90) return 'green';
  if (pct >= 75) return 'amber';
  return 'red';
}

// A RAG-coloured cell. pct null → an em-dash "not applicable" chip, never 0%.
// v126.49 (Matt: "10 tasks set, 6 actioned") — when the backend supplies the
// COUNTS behind the percentage, show them: a % alone says how good, not out of
// what. 6/10 is what you chase a store with. Counts absent → unchanged.
function cell(pct, rag, suffix, done, of) {
  var cls = 'rag-' + (rag || 'none');
  if (pct == null || isNaN(pct)) return '<span class="cr-cell ' + cls + '">—</span>';
  var counts = (done != null && of != null && of > 0)
    ? '<span class="cr-count">' + done + '/' + of + '</span>' : '';
  var title = (done != null && of != null && of > 0) ? ' title="' + of + ' set, ' + done + ' actioned"' : '';
  return '<span class="cr-cell ' + cls + '"' + title + '>' + pct + '%' + (suffix || '') + counts + '</span>';
}
function journalCell(r) {
  var cls = 'rag-' + (r.journalRag || 'none');
  if (r.journalPct == null) return '<span class="cr-cell rag-none">—</span>';
  return '<span class="cr-cell ' + cls + '" title="' + r.journalDays + ' of ' + r.journalOf + ' days">' + r.journalPct + '%</span>';
}

function rangeControls(from, to) {
  return '<div class="cr-range">Range ' +
    '<input type="date" id="crFrom" value="' + esc(from || '') + '">–' +
    '<input type="date" id="crTo" value="' + esc(to || '') + '">' +
    '<button class="cr-btn" onclick="CommsReport.apply()">Apply</button>' +
    // v126.60 (Matt: "we review it on a weekly basis") — one tap for the last
    // COMPLETE week. Before this the only preset was 30 days, so a weekly review
    // meant typing two dates every time.
    '<button class="cr-btn" onclick="CommsReport.lastWeek()">Last week</button>' +
    '<button class="cr-btn" onclick="CommsReport.reset()">Last 30 days</button>' +
    '<button class="cr-btn" onclick="CommsReport.toggleTrend()">📈 Trend</button>' +
    '<button class="cr-btn" onclick="CommsReport.toggleHeat()">🔥 Heat map</button>' +
    '<button class="cr-btn" onclick="CommsReport.toggleCalls()">📹 Calls</button>' +
    // v132.32: not how WELL a store does, but whether it uses the feature
    // at all. The grid above cannot show that: it only covers the four
    // comms features, and a store doing nothing scores the same blank as a
    // store with nothing sent to it.
    '<button class="cr-btn" onclick="CommsReport.toggleAdoption()">📶 Adoption</button>' +
    '<button class="cr-btn" onclick="CommsReport.exportGrid()">⬇ Export</button></div>';
}


// ── Adoption: is this store using this feature AT ALL? (v132.32) ───────────
// The grid above measures how well stores do at what Head Office sent them.
// This one measures something the app could not previously answer: whether a
// store is using a feature at all, and when it last did. A store that
// quietly stopped doing checklists in July was invisible in every view,
// because each report covered its own slice and the nightly figures are
// company averages, which is exactly where one store disappears.
//
// SILENCE IS NOT A FINDING. A store with no chat messages may have nothing
// to say. The wording is 'no sign of use', never 'non-compliant', and the
// panel says so at the top. It is a prompt to go and ask.
var _adoptOpen = false;
var _adoptData = null;
var _adoptDays = 30;

function toggleAdoption() {
  _adoptOpen = !_adoptOpen;
  _trendOpen = false; _heatOpen = false; _callsOpen = false;
  if (_adoptOpen && !_adoptData) { paintAdoption(); fetchAdoption(); }
  else if (_adoptOpen) paintAdoption();
  else if (_lastData) paint(_lastData);
}

function setAdoptionDays(n) {
  var d = parseInt(n, 10);
  _adoptDays = (d === 7 || d === 30 || d === 90) ? d : 30;
  _adoptData = null;
  paintAdoption();
  fetchAdoption();
}

async function fetchAdoption() {
  try {
    var res = await api('/api/admin/adoption?days=' + _adoptDays);
    var d = await res.json().catch(function () { return {}; });
    if (res.ok && d && d.ok) { _adoptData = d; if (_adoptOpen) paintAdoption(); }
    else if (_adoptOpen) { _adoptData = { error: (d && d.error) || 'Not available' }; paintAdoption(); }
  } catch (e) { if (_adoptOpen) { _adoptData = { error: 'Could not load' }; paintAdoption(); } }
}

var ADOPT_CELL = {
  using: { bg: '#009E3D', text: '#fff', mark: 'yes' },
  patchy: { bg: '#F5A623', text: '#1f2937', mark: 'some' },
  silent: { bg: '#e5484d', text: '#fff', mark: 'no' },
};

function adoptCell(f, cell) {
  var look = ADOPT_CELL[cell.verdict] || ADOPT_CELL.silent;
  var when = cell.last_at
    ? (cell.days_since === 0 ? 'today' : (cell.days_since === 1 ? 'yesterday' : cell.days_since + ' days ago'))
    : 'never';
  var detail = f.label + ' - ' + look.mark + '. Last: ' + when + '. ' + cell.events + ' in the window.';
  return '<td style="padding:3px 2px;text-align:center" title="' + esc(detail) + '">' +
    '<span style="display:inline-block;min-width:34px;padding:2px 4px;border-radius:6px;font-size:10.5px;font-weight:800;background:' +
    look.bg + ';color:' + look.text + '">' + esc(look.mark) + '</span></td>';
}

function paintAdoption() {
  var el = root(); if (!el) return;
  var d = _adoptData;
  var picker = '<span style="margin-inline-start:8px;font-size:12px">Window ' +
    [7, 30, 90].map(function (n) {
      return '<button class="cr-btn" onclick="CommsReport.setAdoptionDays(' + n + ')"' +
        (n === _adoptDays ? ' style="background:var(--elc-green);color:#fff"' : '') +
        '>' + n + ' days</button>';
    }).join('') + '</span>';
  var head = '<div class="cr-head"><h3>📶 Feature adoption</h3>' +
    '<div class="cr-range"><button class="cr-btn" onclick="CommsReport.toggleAdoption()">← Back to engagement</button>' + picker + '</div></div>';

  if (!d) { el.innerHTML = '<div class="cr-wrap">' + head + '<div style="padding:24px;color:var(--tiny);font-size:13.5px">Loading…</div></div>'; return; }
  if (d.error) { el.innerHTML = '<div class="cr-wrap">' + head + '<div style="padding:24px;color:var(--tiny)">' + esc(d.error) + '</div></div>'; return; }

  var feats = d.features || [];
  var rows = d.rows || [];
  var legend = '<div class="cr-legend">Whether each store has used each feature at all in the window, and when it last did. ' +
    'This is NOT a compliance score: a store with nothing to say has no chat messages, and store visits are filed by the area manager, not the store. ' +
    'Read a red cell as a reason to ask, not as a finding.' +
    ' <span class="chip" style="background:#009E3D"></span>using' +
    ' <span class="chip" style="background:#F5A623"></span>patchy' +
    ' <span class="chip" style="background:#e5484d"></span>no sign of use</div>';

  var warn = '';
  if (d.unreadable_features && d.unreadable_features.length) {
    warn = '<div style="background:var(--ap-orange-bg);border:1px solid var(--ap-orange-line);border-radius:8px;padding:8px 12px;font-size:12px;color:var(--ap-orange-deep);margin:8px 0">' +
      esc(d.unreadable_features.join(', ')) + ': the table could not be read, so those columns are blank because of a missing table, NOT because the stores are silent.</div>';
  }

  var totals = d.totals || {};
  var summary = '<div style="font-size:12.5px;margin:6px 0 10px">' +
    '<b>' + (totals.stores || 0) + '</b> stores in view. ' +
    '<b>' + (totals.any_silence || 0) + '</b> have at least one feature with no sign of use. ' +
    (totals.fully_silent ? '<b style="color:var(--ui-bad)">' + totals.fully_silent + '</b> show no activity at all, which usually means a data problem rather than an idle store.' : 'None are completely silent.') +
    '</div>';

  var thead = '<tr><th style="text-align:start;padding:4px 8px 4px 0;font-size:11.5px">Store</th>' +
    feats.map(function (f) {
      return '<th style="padding:4px 2px;font-size:10.5px;font-weight:700" title="' + esc(f.matters) + '">' + esc(f.label) + '</th>';
    }).join('') + '</tr>';

  var body = rows.map(function (r) {
    return '<tr>' +
      '<td translate="no" style="padding:3px 8px 3px 0;font-size:12px;white-space:nowrap">' + esc(r.store) + '</td>' +
      feats.map(function (f) { return adoptCell(f, r.features[f.key] || { verdict: 'silent', events: 0, last_at: null }); }).join('') +
    '</tr>';
  }).join('');

  // The list somebody actually acts on: per feature, who to ring.
  var byFeature = feats.map(function (f) {
    var who = (d.silent_by_feature && d.silent_by_feature[f.key]) || [];
    if (!who.length) return '';
    return '<div style="margin-bottom:8px;font-size:12.5px"><b>' + esc(f.label) + '</b> - no sign of use in ' + who.length + ' store(s): ' +
      '<span translate="no">' + esc(who.join(', ')) + '</span><div style="font-size:11.5px;color:var(--tiny)">' + esc(f.matters) + '</div></div>';
  }).join('');

  el.innerHTML = '<div class="cr-wrap">' + head + legend + warn + summary +
    '<div style="overflow-x:auto"><table style="border-collapse:collapse">' + thead + body + '</table></div>' +
    (byFeature ? '<h4 style="margin:16px 0 8px;font-size:13px">Who to ask</h4>' + byFeature : '') +
  '</div>';
}
// ── Excel export (the SVR-Insights pattern; XLSX is loaded by admin.html) ────
function downloadXlsx(sheets, filename) {
  // v136.8: the spreadsheet library loads on first use; this runs again once it has (or says why it could not).
  if (typeof XLSX === 'undefined') { ensureXlsx().then(function () { downloadXlsx(sheets, filename); }, function (e) { alert(e.message); }); return; }
  var wb = XLSX.utils.book_new();
  sheets.forEach(function (s) { XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(s.aoa), String(s.name).slice(0, 31)); });
  XLSX.writeFile(wb, filename);
}
var pctCell = function (v) { return v == null ? '' : v; };

function render() {
  ensureStyles();
  var el = root(); if (!el) return;
  el.innerHTML = '<div class="cr-wrap"><div style="padding:24px;color:var(--tiny);font-size:13.5px">Loading engagement…</div></div>';
  refresh();
}

async function refresh() {
  var el = root(); if (!el) return;
  var q = '';
  q = '?extras=1';   // v132.77: the Google reviews + holiday carry-risk block is opt-in
  if (_from && _to) q += '&from=' + encodeURIComponent(_from) + '&to=' + encodeURIComponent(_to);
  try {
    var res = await api('/api/admin/comms-report' + q);
    var d = await res.json().catch(function () { return {}; });
    if (!res.ok || !d || !d.ok) {
      el.innerHTML = '<div class="cr-wrap"><div style="padding:24px;color:var(--tiny)">' + esc((d && d.error) || 'Not available') + '</div></div>';
      return;
    }
    paint(d);
  } catch (e) {
    el.innerHTML = '<div class="cr-wrap"><div style="padding:24px;color:var(--tiny)">Could not load — check your connection.</div></div>';
  }
}

var _lastData = null;   // the last grid response, so a drill toggle can repaint
var _amFilter = '';     // v127.37 (Matt: "filter by area manager") — '' = all AMs; repaints client-side, no refetch
// v129.82 (Matt: "track compliance at store level as well as individual") —
// 'store' = one person actioning an item makes the store compliant (default);
// 'people' = the original everyone-counted view. Drill-down is identical in both.
var _view = 'store';

function paint(d) {
  var el = root(); if (!el) return;
  _lastData = d;
  _trendOpen = false;   // painting the grid always leaves trend mode
  _heatOpen = false;    // …and heat-map mode (v125.69)
  _adoptOpen = false;   // …and the adoption view (v132.32)
  var rep = (_view === 'store' && d.report_store) ? d.report_store : (d.report || {});
  var allRows = rep.rows || [];
  var company = rep.company || {};
  var areas = rep.areas || [];
  var ragOf = ragFor;

  // v127.37 — the AM filter narrows the grid to one manager's stores. The
  // summary row swaps to the existing per-AM rollup (same numbers as the
  // "By area manager" table — never re-derived client-side), and everything
  // repaints from _lastData without a refetch.
  var amNames = [];
  allRows.forEach(function (r) {
    var am = r.area_manager || '';
    if (am && amNames.indexOf(am) === -1) amNames.push(am);
  });
  amNames.sort();
  if (_amFilter && amNames.indexOf(_amFilter) === -1) _amFilter = '';   // stale filter after a range change
  var rows = _amFilter ? allRows.filter(function (r) { return (r.area_manager || '') === _amFilter; }) : allRows;

  var amSelect = amNames.length > 1
    ? '<select id="crAm" onchange="CommsReport.amFilter(this.value)" style="font-family:inherit;font-size:12px;padding:4px 6px;border:1px solid var(--border-strong);border-radius:8px;margin-inline-start:8px">' +
      '<option value="">All area managers</option>' +
      amNames.map(function (n) { return '<option translate="no" value="' + esc(n) + '"' + (n === _amFilter ? ' selected' : '') + '>' + esc(n) + '</option>'; }).join('') +
      '</select>'
    : '';
  // v129.82 — the store/people toggle (only when the server sent both views).
  var viewToggle = d.report_store
    ? '<span style="margin-inline-start:8px;font-size:12px;white-space:nowrap">' +
      '<button onclick="CommsReport.setView(\'store\')" style="font-family:inherit;font-size:11.5px;font-weight:800;padding:4px 9px;border-radius:8px 0 0 8px;border:1px solid var(--border-strong);cursor:pointer;' + (_view === 'store' ? 'background:var(--elc-green);color:#fff' : 'background:var(--card);color:var(--text)') + '">🏪 Store</button>' +
      '<button onclick="CommsReport.setView(\'people\')" style="font-family:inherit;font-size:11.5px;font-weight:800;padding:4px 9px;border-radius:0 8px 8px 0;border:1px solid var(--border-strong);border-left:none;cursor:pointer;' + (_view === 'people' ? 'background:var(--elc-green);color:#fff' : 'background:var(--card);color:var(--text)') + '">👤 People</button>' +
      '</span>'
    : '';
  var viewNote = d.report_store && _view === 'store'
    ? '<div style="font-size:11.5px;color:var(--tiny);margin:2px 0 8px">Store view: an item counts when <b>at least one person</b> in the store has actioned it. Switch to 👤 People for the everyone-counted view; drill into a store for names either way.</div>'
    : '';
  var head = '<div class="cr-head"><h3>📊 Comms engagement</h3>' + rangeControls(d.from, d.to) + amSelect + viewToggle + '</div>' + viewNote +
    '<div class="cr-legend">How much of what Head Office sent down has been actioned, per store. Click a store row to see exactly who is missing.' +
    ' <span class="chip rag-green" style="background:#009E3D"></span>≥90%' +
    ' <span class="chip rag-amber" style="background:#F5A623"></span>75–89%' +
    ' <span class="chip rag-red" style="background:#E24A4A"></span>&lt;75%' +
    ' <span class="chip rag-none" style="background:var(--surface-3)"></span>no data</div>';

  _lastRows = rows;
  var body = rows.map(function (r, i) {
    var open = _openStore === r.store;
    var tr = '<tr class="cr-row" style="cursor:pointer" onclick="CommsReport.drill(' + i + ')" title="Click to see who is missing">' +
      '<td class="store" translate="no">' + (open ? '▾ ' : '▸ ') + esc(r.store) + '</td>' +
      '<td class="am" translate="no">' + esc(r.area_manager || '') + '</td>' +
      '<td>' + cell(r.news, r.newsRag, '', r.newsDone, r.newsOf) + '</td>' +
      '<td>' + cell(r.tasks, r.tasksRag, '', r.tasksDone, r.tasksOf) + '</td>' +
      '<td>' + cell(r.surveys, r.surveysRag, '', r.surveysDone, r.surveysOf) + '</td>' +
      '<td>' + journalCell(r) + '</td>' +
      '</tr>';
    if (open) tr += '<tr class="cr-drill"><td colspan="6" style="text-align:start;white-space:normal">' + drillHtml(r.store) + '</td></tr>';
    return tr;
  }).join('');

  // Company roll-up row (RAG re-derived from the average via the shared core).
  // v127.37: filtered to one AM → the row IS that AM's rollup (label + numbers).
  var sum = company, sumLabel = 'All stores';
  if (_amFilter) {
    var amAgg = areas.filter(function (a) { return a.area_manager === _amFilter; })[0];
    if (amAgg) { sum = amAgg; sumLabel = _amFilter; }
  }
  var companyRow = '<tr class="cr-sub">' +
    '<td class="store"' + (_amFilter ? ' translate="no"' : '') + '>' + esc(sumLabel) + '</td><td class="am">' + rows.length + ' store(s)</td>' +
    '<td>' + cell(sum.news, ragOf(sum.news)) + '</td>' +
    '<td>' + cell(sum.tasks, ragOf(sum.tasks)) + '</td>' +
    '<td>' + cell(sum.surveys, ragOf(sum.surveys)) + '</td>' +
    '<td>' + cell(sum.journal, ragOf(sum.journal)) + '</td></tr>';

  var table = '<div class="cr-scroll"><table>' +
    '<thead><tr><th class="store">Store</th><th class="store">Area manager</th>' +
    '<th>📰 News</th><th>☑️ Tasks</th><th>📋 Surveys</th><th>📔 Journal</th></tr></thead>' +
    '<tbody>' + companyRow + (body || '<tr><td colspan="6" style="padding:24px;color:var(--tiny)">No stores in scope.</td></tr>') + '</tbody></table></div>';

  // Per-area-manager roll-up (only worth showing when more than one AM is in scope).
  var areaBlock = '';
  if (areas.length > 1 && !_amFilter) {   // v127.37: redundant when one AM is filtered
    var arows = areas.map(function (a) {
      return '<tr><td class="store" translate="no">' + esc(a.area_manager) + '</td><td class="am">' + a.stores + ' store(s)</td>' +
        '<td>' + cell(a.news, ragOf(a.news)) + '</td>' +
        '<td>' + cell(a.tasks, ragOf(a.tasks)) + '</td>' +
        '<td>' + cell(a.surveys, ragOf(a.surveys)) + '</td>' +
        '<td>' + cell(a.journal, ragOf(a.journal)) + '</td></tr>';
    }).join('');
    areaBlock = '<h3 style="font-size:14px;color:var(--brand);margin:20px 0 8px">By area manager</h3>' +
      '<div class="cr-scroll"><table><thead><tr><th class="store">Area manager</th><th class="store">Stores</th>' +
      '<th>📰 News</th><th>☑️ Tasks</th><th>📋 Surveys</th><th>📔 Journal</th></tr></thead><tbody>' + arows + '</tbody></table></div>';
  }

  el.innerHTML = '<div class="cr-wrap">' + head + table + areaBlock + extrasHtml(d.extras, rows) +   // v132.72
    '<div class="cr-note">News = staff who confirmed a post · Tasks = due tasks completed on time · ' +
    'Surveys = targeted staff who answered · Journal = days a store note was written, over the window. ' +
    'Read-only; no pay or personal data.</div></div>';
}

// v132.72 (Matt: "Is the holiday risk in the admin/comms/tradepack?"): the
// Google reviews figures and the holiday carry risk, per store, under the
// compliance table. Same numbers as the Monday emails; nothing counted here.
function extrasHtml(ex, rows) {
  if (!ex || (!ex.google && !ex.holiday)) return '';
  var g = ex.google || {}, h = ex.holiday || {};
  var dash = '<span style="color:var(--tiny)">\u2013</span>';
  var body = (rows || []).map(function (r) {
    var gg = g[r.store], hh = h[r.store];
    var ratingTxt = gg && gg.rating != null ? Number(gg.rating).toFixed(1) : dash;
    var newTxt = gg && gg.gained_7d != null ? (gg.gained_7d > 0 ? '+' + gg.gained_7d : String(gg.gained_7d)) : dash;
    var lastTxt = gg && gg.days_since_review != null ? gg.days_since_review + 'd' : dash;
    var newColour = gg && gg.gained_7d > 0 ? 'var(--ap-teal-text)' : (gg && gg.quiet && !gg.no_snapshot ? 'var(--danger-deep)' : 'inherit');
    var risksTxt = hh ? '<b style="color:var(--danger-deep)">' + hh.risks + '</b>' : (ex.holiday ? '0' : dash);
    var mustTxt = hh ? hh.must_book + 'd' : (ex.holiday ? dash : dash);
    return '<tr><td class="store" translate="no">' + esc(r.store) + '</td>' +
      '<td>' + ratingTxt + '</td><td>' + (gg && gg.review_count != null ? gg.review_count : dash) + '</td>' +
      '<td style="color:' + newColour + '">' + newTxt + '</td><td>' + lastTxt + '</td>' +
      '<td>' + risksTxt + '</td><td>' + mustTxt + '</td></tr>';
  }).join('');
  return '<div style="margin-top:18px;font-weight:700;font-size:13px">\u2B50 Google reviews and \uD83C\uDFD6 holiday carry risk</div>' +
    '<div class="cr-scroll"><table><thead><tr><th class="store">Store</th>' +
    '<th>Rating</th><th>Reviews</th><th>New (7d)</th><th>Last review</th><th>Carry risks</th><th>Must book</th></tr></thead>' +
    '<tbody>' + body + '</tbody></table></div>' +
    '<div class="cr-note">Google: real reviews per listing from the nightly snapshot (Dubai Mall 1 and 2 share one listing); a dash means no snapshot yet. ' +
    'Carry risk: people heading past the ' + CARRY_FLAG_DAYS + '-day holiday limit at year end after the leave already booked, and the minimum they must book; the same rows as the Monday holiday email.</div>';
}
// Range state (null → the endpoint's default last-30-days).
var _from = null;
var _to = null;
// Drill-down state: the last-painted rows (index-addressed — store names are
// never inlined into onclick), which store is open, and its fetched detail.
var _lastRows = [];
var _openStore = null;
var _detail = null;

function apply() {
  var f = (document.getElementById('crFrom') || {}).value || '';
  var t = (document.getElementById('crTo') || {}).value || '';
  if (f && t && f <= t) { _from = f; _to = t; _openStore = null; _detail = null; refresh(); }
}
function reset() { _from = null; _to = null; _openStore = null; _detail = null; refresh(); }

// v126.60 — the last COMPLETE Monday–Sunday week.
//
// Monday–Sunday deliberately: the 📈 Trend view already buckets into "completed
// Mon–Sun weeks" (src/worker/comms-trend.js), so this button and that chart now
// mean the same thing by "a week". Two different week definitions in one report
// would be worse than no button.
//
// "Complete" also matters — reviewing on a Monday, you want LAST week finished,
// not this week half-done. Run it on a Monday and you get the seven days that
// just ended; run it midweek and you still get that same finished week.
//
// Dates are built in LOCAL time (getDay/getDate), not UTC. This picks a whole
// calendar week for a person reading a report, and today's news-fixture time
// bomb is a fresh reminder that a UTC boundary can silently shift a day.
export function lastCompleteWeek(today) {
  var d = today ? new Date(today) : new Date();
  d.setHours(12, 0, 0, 0);                 // midday: immune to DST edges
  var dow = d.getDay();                    // 0=Sun … 1=Mon
  var backToThisMonday = (dow === 0) ? 6 : (dow - 1);
  var thisMonday = new Date(d); thisMonday.setDate(d.getDate() - backToThisMonday);
  var lastMonday = new Date(thisMonday); lastMonday.setDate(thisMonday.getDate() - 7);
  var lastSunday = new Date(lastMonday); lastSunday.setDate(lastMonday.getDate() + 6);
  var ymd = function (x) {
    return x.getFullYear() + '-' + String(x.getMonth() + 1).padStart(2, '0') + '-' + String(x.getDate()).padStart(2, '0');
  };
  return { from: ymd(lastMonday), to: ymd(lastSunday) };
}

function lastWeek() {
  var w = lastCompleteWeek();
  _from = w.from; _to = w.to; _openStore = null; _detail = null;
  refresh();
}

// ── Drill-down: who exactly is missing, per store ─────────────────────────────
function nameList(names, cap) {
  var xs = (Array.isArray(names) ? names : []).map(esc);
  cap = cap || 12;
  if (!xs.length) return '<span class="cr-ok">everyone done ✓</span>';
  var shown = xs.slice(0, cap).join(', ');
  return '<span translate="no">' + shown + '</span>' + (xs.length > cap ? ' <span class="cr-more">+' + (xs.length - cap) + ' more</span>' : '');
}
function drillHtml(store) {
  if (!_detail || _detail._store !== store) return '<div class="cr-dwrap">Loading who is missing…</div>';
  var d = _detail;
  var h = '<div class="cr-dwrap">';
  h += '<div class="cr-dhead"><span translate="no">' + esc(store) + '</span> — who is missing (' + esc(d._from || '') + ' to ' + esc(d._to || '') + ')</div>';
  h += '<div class="cr-dsec"><b>📰 News</b>' + ((d.news || []).length
    ? (d.news || []).map(function (p) {
        return '<div class="cr-ditem"><span class="cr-dtitle" translate="no">' + esc(p.title) + '</span> — ' + p.confirmed + '/' + p.total + ' read. ' +
          (p.missing.length ? 'Not read: ' + nameList(p.missing) : '<span class="cr-ok">all read ✓</span>') + '</div>';
      }).join('')
    : '<div class="cr-ditem cr-dim">No live news posts target this store.</div>') + '</div>';
  var tk = d.tasks || { due: 0, done: 0, missing: [] };
  h += '<div class="cr-dsec"><b>☑️ Tasks</b>' + (tk.due
    ? '<div class="cr-ditem">' + tk.done + '/' + tk.due + ' done in the window.' +
      (tk.missing.length ? ' Missed: <span translate="no">' + tk.missing.slice(0, 12).map(function (m) { return esc(m.task) + ' (' + esc(m.date) + ')'; }).join(', ') + '</span>' +
        (tk.missing.length > 12 ? ' <span class="cr-more">+' + (tk.missing.length - 12) + ' more</span>' : '') : ' <span class="cr-ok">nothing missed ✓</span>') + '</div>'
    : '<div class="cr-ditem cr-dim">No tasks were due for this store in the window.</div>') + '</div>';
  h += '<div class="cr-dsec"><b>📋 Surveys</b>' + ((d.surveys || []).length
    ? (d.surveys || []).map(function (s) {
        return '<div class="cr-ditem"><span class="cr-dtitle" translate="no">' + esc(s.title) + '</span> — ' + s.done + '/' + s.total + ' answered. ' +
          (s.missing.length ? 'Missing: ' + nameList(s.missing) : '<span class="cr-ok">all answered ✓</span>') + '</div>';
      }).join('')
    : '<div class="cr-ditem cr-dim">No live surveys target this store.</div>') + '</div>';
  h += '<div class="cr-dsec"><b>📔 Journal</b><div class="cr-ditem">' +
    (d.journal ? d.journal.days + ' of ' + d.journal.of + ' day(s) written.' : '<span class="cr-dim">No journal entries in the window.</span>') + '</div></div>';
  h += '</div>';
  return h;
}
// ── Grid export: exactly what's on screen — Stores + Areas sheets ─────────────
function exportGrid() {
  if (!_lastData || !_lastData.report) { if (typeof showToast !== 'undefined') showToast('Nothing to export yet — let the grid load first.', 'error'); return; }
  var rep = _lastData.report;
  var head = ['Store', 'Area manager', 'News read %', 'Tasks done %', 'Surveys %', 'Journal days', 'Journal of', 'Journal %'];
  var storesAoa = [head].concat((rep.rows || []).map(function (r) {
    return [r.store, r.area_manager || '', pctCell(r.news), pctCell(r.tasks), pctCell(r.surveys), pctCell(r.journalDays), pctCell(r.journalOf), pctCell(r.journalPct)];
  }));
  var c = rep.company || {};
  storesAoa.push(['All stores', '', pctCell(c.news), pctCell(c.tasks), pctCell(c.surveys), '', '', pctCell(c.journal)]);
  var areasAoa = [['Area manager', 'Stores', 'News read %', 'Tasks done %', 'Surveys %', 'Journal %']]
    .concat((rep.areas || []).map(function (a) {
      return [a.area_manager, a.stores, pctCell(a.news), pctCell(a.tasks), pctCell(a.surveys), pctCell(a.journal)];
    }));
  downloadXlsx(
    [{ name: 'Stores', aoa: storesAoa }, { name: 'Areas', aoa: areasAoa }],
    'comms-report_' + (_lastData.from || '') + '_' + (_lastData.to || '') + '.xlsx'
  );
}

// ── Trend view (A2): weeks × areas per metric, from /api/admin/comms-trend ───
var _trendOpen = false;
var _trendData = null;
var _trendMetric = 'news';
var TREND_METRICS = [['news', '📰 News read %'], ['tasks', '☑️ Tasks done %'], ['surveys', '📋 Surveys %'], ['journal', '📔 Journal %']];

function toggleTrend() {
  _trendOpen = !_trendOpen;
  _heatOpen = false;                       // v125.69: the views are exclusive
  _callsOpen = false;
  if (_trendOpen && !_trendData) { paintTrend(); fetchTrend(); }
  else if (_trendOpen) paintTrend();
  else if (_lastData) paint(_lastData);
}

// ── v125.69 — chat heat map (Matt: "admin should also have an ai summary and
// heat map"): stores × recent business days, coloured by messages sent ────────
var _heatOpen = false;
var _heatData = null;

function toggleHeat() {
  _heatOpen = !_heatOpen;
  _trendOpen = false;
  _callsOpen = false;
  if (_heatOpen && !_heatData) { paintHeat(); fetchHeat(); }
  else if (_heatOpen) paintHeat();
  else if (_lastData) paint(_lastData);
}

// ── v126.16 — 📹 Calls usage (Matt: "can i get usage updates for store to
// store calls?"): join taps from calls_log, rolled up by type + store ────────
var _callsOpen = false;
var _callsData = null;

function toggleCalls() {
  _callsOpen = !_callsOpen;
  _heatOpen = false; _trendOpen = false;
  if (_callsOpen && !_callsData) { paintCalls(); fetchCalls(); }
  else if (_callsOpen) paintCalls();
  else if (_lastData) paint(_lastData);
}
async function fetchCalls() {
  try {
    var res = await api('/api/calls/usage?days=30');
    var d = await res.json().catch(function () { return {}; });
    if (res.ok && d && d.ok) { _callsData = d; if (_callsOpen) paintCalls(); }
    else if (_callsOpen) paintCalls('Not available' + (d && d.error ? ' — ' + d.error : ''));
  } catch (e) { if (_callsOpen) paintCalls('Could not load — check your connection.'); }
}
var CALL_KIND_LABELS = { area: '📹 Area calls', store: '🏪 Own store room', store_to_store: '📞 Store-to-store', custom: '🎤 Ad-hoc meetings', task: '📅 Task meetings' };
function paintCalls(errMsg) {
  var el = root(); if (!el) return;
  var head = '<div class="cr-head"><h3>📹 Calls usage — join taps, last 30 days</h3>' +
    '<div class="cr-range"><button class="cr-btn" onclick="CommsReport.toggleCalls()">↩ Back to the grid</button></div></div>' +
    '<div class="cr-legend">Each row is a tap on a Join button in the app. We never see the call itself (it runs on Jitsi) — this is who is USING calls, not what was said. Needs the v162 migration.</div>';
  if (errMsg) { el.innerHTML = '<div class="cr-wrap">' + head + '<div style="padding:24px;color:var(--tiny)">' + esc(errMsg) + '</div></div>'; return; }
  if (!_callsData) { el.innerHTML = '<div class="cr-wrap">' + head + '<div style="padding:24px;color:var(--tiny)">Counting joins…</div></div>'; return; }
  var d = _callsData;
  var chips = Object.keys(d.by_kind || {}).map(function (k) {
    return '<span class="cr-cell rag-none" style="margin-inline-end:6px">' + esc(CALL_KIND_LABELS[k] || k) + ': <b>' + d.by_kind[k] + '</b></span>';
  }).join('') || '<span style="color:var(--tiny)">No joins recorded yet — the buttons only started counting on v126.16.</span>';
  var storeRows = Object.keys(d.by_store || {}).sort(function (a, b) { return d.by_store[b] - d.by_store[a]; })
    .map(function (s) { return '<tr><td class="store" translate="no">' + esc(s) + '</td><td style="text-align:center;font-weight:700">' + d.by_store[s] + '</td></tr>'; }).join('');
  var recent = (d.recent || []).map(function (r) {
    return '<tr><td>' + esc(String(r.joined_at || '').slice(0, 16).replace('T', ' ')) + '</td><td translate="no">' + esc(r.store || r.joined_by || '—') + '</td>' +
      '<td>' + esc(CALL_KIND_LABELS[r.kind] || r.kind || '') + '</td><td translate="no">' + esc(r.target || '') + '</td></tr>';
  }).join('');
  el.innerHTML = '<div class="cr-wrap">' + head +
    '<div style="padding:10px 0 14px">' + chips + '</div>' +
    (storeRows ? '<div class="cr-scroll"><table><thead><tr><th class="store">Who</th><th>Joins</th></tr></thead><tbody>' + storeRows + '</tbody></table></div>' : '') +
    (recent ? '<div style="margin-top:14px;font-weight:700;font-size:13px">Most recent</div><div class="cr-scroll"><table><thead><tr><th>When (UTC)</th><th>Who</th><th>Type</th><th>Room</th></tr></thead><tbody>' + recent + '</tbody></table></div>' : '') +
    '<div class="cr-note">Total: ' + (d.total || 0) + ' join' + (d.total === 1 ? '' : 's') + ' in the last ' + (d.days || 30) + ' days.</div></div>';
}
async function fetchHeat() {
  try {
    var res = await api('/api/admin/chat-heatmap?days=14');
    var d = await res.json().catch(function () { return {}; });
    if (res.ok && d && d.ok) { _heatData = d; if (_heatOpen) paintHeat(); }
    else if (_heatOpen) paintHeat('Not available' + (d && d.error ? ' — ' + d.error : ''));
  } catch (e) { if (_heatOpen) paintHeat('Could not load — check your connection.'); }
}
// 5-step scale: empty grey → light green → brand green → deep teal (busiest).
function heatColor(n, max) {
  if (!n) return { bg: 'var(--surface-3)', fg: 'var(--tiny)' };
  var r = max > 0 ? n / max : 0;
  if (r > 0.75) return { bg: '#075e54', fg: '#fff' };
  if (r > 0.5) return { bg: '#009E3D', fg: '#fff' };
  if (r > 0.25) return { bg: '#59c26f', fg: '#fff' };
  return { bg: '#d9fdd3', fg: '#1b4332' };
}
function heatDayLabel(ymd) { return String(ymd || '').slice(8) + '/' + String(ymd || '').slice(5, 7); }   // DD/MM
function paintHeat(errMsg) {
  var el = root(); if (!el) return;
  var head = '<div class="cr-head"><h3>🔥 Chat heat map — messages per store</h3>' +
    '<div class="cr-range"><button class="cr-btn" onclick="CommsReport.toggleHeat()">↩ Back to the grid</button></div></div>' +
    '<div class="cr-legend">Chat messages each store\'s staff sent per day (3am–3am business days, last 14 days). Darker = busier; Head Office posts don\'t count towards a store.</div>';
  if (errMsg) { el.innerHTML = '<div class="cr-wrap">' + head + '<div style="padding:24px;color:var(--tiny)">' + esc(errMsg) + '</div></div>'; return; }
  if (!_heatData) { el.innerHTML = '<div class="cr-wrap">' + head + '<div style="padding:24px;color:var(--tiny)">Counting messages…</div></div>'; return; }
  var days = _heatData.days || [];
  var max = _heatData.max || 0;
  var rows = (_heatData.stores || []).map(function (s) {
    return '<tr><td class="store" translate="no">' + esc(s.store) + '</td>' +
      (s.byDay || []).map(function (n, i) {
        var c = heatColor(n, max);
        return '<td style="background:' + c.bg + ';color:' + c.fg + ';text-align:center;min-width:32px" title="' +
          esc(s.store) + ' — ' + esc(days[i] || '') + ': ' + n + ' message' + (n === 1 ? '' : 's') + '">' + (n || '') + '</td>';
      }).join('') +
      '<td style="text-align:center;font-weight:700">' + (s.total || 0) + '</td></tr>';
  }).join('');
  var table = '<div class="cr-scroll"><table><thead><tr><th class="store">Store</th>' +
    days.map(function (d) { return '<th>' + esc(heatDayLabel(d)) + '</th>'; }).join('') +
    '<th>Total</th></tr></thead><tbody>' + rows + '</tbody></table></div>';
  el.innerHTML = '<div class="cr-wrap">' + head + table +
    '<div class="cr-note">Quiet rows at the bottom are stores not using chat — worth a nudge.</div></div>';
}
async function fetchTrend() {
  try {
    var res = await api('/api/admin/comms-trend?weeks=8');
    var d = await res.json().catch(function () { return {}; });
    if (res.ok && d && d.ok) { _trendData = d; if (_trendOpen) paintTrend(); }
    else if (_trendOpen) paintTrend('Not available' + (d && d.error ? ' — ' + d.error : ''));
  } catch (e) { if (_trendOpen) paintTrend('Could not load — check your connection.'); }
}
function setTrendMetric(m) {
  if (TREND_METRICS.some(function (x) { return x[0] === m; })) { _trendMetric = m; paintTrend(); }
}
function weekLabel(ws) { return String(ws || '').slice(5); }   // 'MM-DD' of the Monday
function paintTrend(errMsg) {
  var el = root(); if (!el) return;
  var head = '<div class="cr-head"><h3>📈 Comms engagement — week on week</h3>' +
    '<div class="cr-range">' +
    TREND_METRICS.map(function (m) {
      return '<button class="cr-btn" style="' + (m[0] === _trendMetric ? 'background:var(--brand);color:#fff' : '') + '" onclick="CommsReport.setTrendMetric(\'' + m[0] + '\')">' + m[1] + '</button>';
    }).join('') +
    '<button class="cr-btn" onclick="CommsReport.exportTrend()">⬇ Export</button>' +
    '<button class="cr-btn" onclick="CommsReport.toggleTrend()">↩ Back to the grid</button></div></div>';
  if (errMsg) { el.innerHTML = '<div class="cr-wrap">' + head + '<div style="padding:24px;color:var(--tiny)">' + esc(errMsg) + '</div></div>'; return; }
  if (!_trendData) { el.innerHTML = '<div class="cr-wrap">' + head + '<div style="padding:24px;color:var(--tiny)">Loading the last 8 completed weeks…</div></div>'; return; }
  var weeks = _trendData.weeks || [];
  var m = _trendMetric;
  var rowFor = function (label, series, bold) {
    return '<tr' + (bold ? ' class="cr-sub"' : '') + '><td class="store"' + (bold ? '' : ' translate="no"') + '>' + esc(label) + '</td>' +
      weeks.map(function (ws, i) {
        var v = series[i] ? series[i][m] : null;
        return '<td>' + cell(v, ragFor(v)) + '</td>';
      }).join('') + '</tr>';
  };
  var table = '<div class="cr-scroll"><table><thead><tr><th class="store">Area manager</th>' +
    weeks.map(function (ws) { return '<th>wk ' + esc(weekLabel(ws)) + '</th>'; }).join('') +
    '</tr></thead><tbody>' +
    rowFor('All stores', _trendData.company || [], true) +
    (_trendData.areas || []).map(function (a) { return rowFor(a.area_manager, a.series || [], false); }).join('') +
    '</tbody></table></div>';
  var tr = _trendData.trend && _trendData.trend[m];
  var direction = tr
    ? ('Company: ' + tr.first + '% → ' + tr.last + '% (' + (tr.change > 0 ? '+' : '') + tr.change + ' pts over the period)')
    : 'Not enough weekly data yet — history builds as the platform is used.';
  el.innerHTML = '<div class="cr-wrap">' + head + table +
    '<div class="cr-note"><span>' + esc(direction) + '</span> · News/surveys are "% done as of that week\'s end" (today\'s roster); tasks/journal are exact history. Completed Mon–Sun weeks only.</div></div>';
}
function exportTrend() {
  if (!_trendData) { if (typeof showToast !== 'undefined') showToast('Nothing to export yet.', 'error'); return; }
  var weeks = _trendData.weeks || [];
  var sheets = TREND_METRICS.map(function (mm) {
    var m = mm[0];
    var aoa = [['Area manager'].concat(weeks.map(function (ws) { return 'wk ' + weekLabel(ws); }))];
    aoa.push(['All stores'].concat((_trendData.company || []).map(function (cw) { return pctCell(cw[m]); })));
    (_trendData.areas || []).forEach(function (a) {
      aoa.push([a.area_manager].concat((a.series || []).map(function (sw) { return pctCell(sw[m]); })));
    });
    return { name: mm[1].replace(/[^\w %]/g, '').trim(), aoa: aoa };
  });
  downloadXlsx(sheets, 'comms-trend_' + (weeks[0] || '') + '_' + (weeks[weeks.length - 1] || '') + '.xlsx');
}

async function drill(i) {
  var r = _lastRows[i];
  if (!r) return;
  if (_openStore === r.store) { _openStore = null; _detail = null; if (_lastData) paint(_lastData); return; }
  _openStore = r.store; _detail = null;
  if (_lastData) paint(_lastData);                          // show the loading row now
  var q = '?store=' + encodeURIComponent(r.store);
  if (_from && _to) q += '&from=' + encodeURIComponent(_from) + '&to=' + encodeURIComponent(_to);
  try {
    var res = await api('/api/admin/comms-report' + q);
    var d = await res.json().catch(function () { return {}; });
    if (res.ok && d && d.ok && _openStore === r.store) {
      _detail = d.detail || {};
      _detail._store = r.store; _detail._from = d.from; _detail._to = d.to;
      if (_lastData) paint(_lastData);
    }
  } catch (e) { /* the loading row stays; a re-click retries */ }
}

// v125.14 CRITICAL: handlers are EXPORTED, not on `window.CommsReport = {…}` —
// esbuild's `globalName:'CommsReport'` assigns the module EXPORTS to
// window.CommsReport, clobbering any in-body assignment. (See src/news.)
// v127.37 — AM filter handler: repaint from the held response, no refetch.
function amFilter(v) { _amFilter = String(v || ''); _openStore = null; if (_lastData) paint(_lastData); }

function setView(v) {
  _view = (v === 'people') ? 'people' : 'store';
  if (_lastData) paint(_lastData);
}

export { esc, render, apply, reset, lastWeek, drill, toggleTrend, exportGrid, exportTrend, setTrendMetric, toggleHeat, toggleCalls, amFilter, setView, toggleAdoption, setAdoptionDays };
